import { apiJson } from "../../../shared/lib/api-client.ts";
import type { ApiResponse } from "../../leaderboard/types/index.ts";
import type {
  AdminOwnerOption,
  EvAgentContext,
  EvBriefPayload,
  EvCallPrepArtifact,
  EvCallPrepStatus,
  EvCallReadyRequest,
  EvCaptureTelegramRelationshipRequest,
  EvCaptureTwitterRelationshipRequest,
  EvContactSearchParams,
  EvContactSearchResult,
  EvGoogleCalendarEventCreateRequest,
  EvGoogleCalendarEventPayload,
  EvGoogleCalendarEventUpdateRequest,
  EvGoogleConnectionStatusPayload,
  EvGoogleConnectStartPayload,
  EvGoogleGmailDraftPayload,
  EvGoogleGmailDraftRequest,
  EvGoogleGmailSendPayload,
  EvGoogleGmailSendRequest,
  EvGoogleRefreshPayload,
  EvIntroContactUpdateInput,
  EvLinkedInAttachRequest,
  EvMessageLogRequest,
  EvPriorityActionInput,
  EvProjectArtifact,
  EvRelationshipTargetUpdateRequest,
  EvRelationshipUpdateRequest,
  EvTelegramMtprotoStatusPayload,
  EvTelegramSendPayload,
  EvTelegramSendRequest,
  EvTouchpointRequest,
  EvWorkflowEventPayload,
  EvWorkflowPatch,
  EvWorkstationDetail,
  EvWorkstationFilters,
  EvWorkstationProject,
  EvWorkstationSummary,
  EvXAuditRecord,
  EvXConnectionStatusPayload,
  EvXDraft,
  EvXMessageRecord,
  EvXPreviewPayload,
  EvXPreviewRequest,
  EvXPromptSettingsPayload,
  EvXQueueContact,
  EvXResolveHandlePayload,
  EvXSendPayload,
  EvXSendRequest,
} from "../types/index.ts";

function buildQueryString(filters: EvWorkstationFilters = {}): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (!value || value === "all") continue;
    params.set(key, value);
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}

function buildContactQueryString(params: EvContactSearchParams = {}): string {
  const searchParams = new URLSearchParams();

  if (params.search?.trim()) searchParams.set("search", params.search.trim());
  if (params.ownerScope && params.ownerScope !== "all") {
    searchParams.set("ownerScope", params.ownerScope);
  }
  if (params.limit) searchParams.set("limit", String(params.limit));

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export async function getEvWorkstationProjects(
  filters: EvWorkstationFilters = {}
): Promise<EvWorkstationProject[]> {
  const payload = await apiJson<ApiResponse<EvWorkstationProject[]>>(
    `/v1/ev-workstation/projects${buildQueryString(filters)}`
  );
  return payload.data ?? [];
}

export async function getEvWorkstationProjectDetail(
  projectId: string
): Promise<EvWorkstationDetail | null> {
  const payload = await apiJson<ApiResponse<EvWorkstationDetail>>(
    `/v1/ev-workstation/projects/${projectId}`
  );
  return payload.data ?? null;
}

export async function getEvWorkstationProjectSummary(
  projectId: string
): Promise<EvWorkstationProject | null> {
  const payload = await apiJson<ApiResponse<EvWorkstationProject>>(
    `/v1/ev-workstation/projects/${projectId}/summary`
  );
  return payload.data ?? null;
}

export async function getEvAgentContext(projectId: string): Promise<EvAgentContext | null> {
  const payload = await apiJson<ApiResponse<EvAgentContext>>(
    `/v1/ev-workstation/projects/${projectId}/agent-context`
  );
  return payload.data ?? null;
}

export async function getEvProjectCallPrep(
  projectId: string
): Promise<EvProjectArtifact<EvCallPrepArtifact> | null> {
  const payload = await apiJson<ApiResponse<EvProjectArtifact<EvCallPrepArtifact>>>(
    `/v1/ev-workstation/projects/${projectId}/call-prep`
  );
  return payload.data ?? null;
}

export async function getEvProjectCallPrepStatus(
  projectId: string
): Promise<EvCallPrepStatus | null> {
  const payload = await apiJson<ApiResponse<EvCallPrepStatus>>(
    `/v1/ev-workstation/projects/${projectId}/call-prep/status`
  );
  return payload.data ?? null;
}

export async function regenerateEvProjectCallPrep(
  projectId: string
): Promise<EvCallPrepStatus | null> {
  const payload = await apiJson<ApiResponse<EvCallPrepStatus>>(
    `/v1/ev-workstation/projects/${projectId}/call-prep`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: "regenerate" }),
    }
  );
  return payload.data ?? null;
}

export async function prepareEvProjectCallReady(
  projectId: string,
  request: EvCallReadyRequest
): Promise<EvWorkstationDetail | null> {
  const payload = await apiJson<ApiResponse<EvWorkstationDetail>>(
    `/v1/ev-workstation/projects/${projectId}/call-prep`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: "generate", ...request }),
    }
  );

  return payload.data ?? null;
}

export async function updateEvWorkflow(
  projectId: string,
  patch: EvWorkflowPatch
): Promise<EvWorkstationDetail | null> {
  const payload = await apiJson<ApiResponse<EvWorkstationDetail>>(
    `/v1/ev-workstation/projects/${projectId}/workflow`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }
  );

  return payload.data ?? null;
}

export async function updateEvIntroContact(
  projectId: string,
  input: EvIntroContactUpdateInput
): Promise<EvWorkstationDetail | null> {
  const payload = await apiJson<ApiResponse<EvWorkstationDetail>>(
    `/v1/ev-workstation/projects/${projectId}/intro-contacts`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }
  );

  return payload.data ?? null;
}

export async function applyEvPriorityAction(
  projectId: string,
  input: EvPriorityActionInput
): Promise<EvWorkstationDetail | null> {
  const payload = await apiJson<ApiResponse<EvWorkstationDetail>>(
    `/v1/ev-workstation/projects/${projectId}/priority-actions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }
  );

  return payload.data ?? null;
}

export async function createEvWorkflowEvent(
  projectId: string,
  event: EvWorkflowEventPayload
): Promise<EvWorkstationDetail | null> {
  const payload = await apiJson<ApiResponse<EvWorkstationDetail>>(
    `/v1/ev-workstation/projects/${projectId}/events`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
    }
  );

  return payload.data ?? null;
}

export async function getEvFollowUps(): Promise<EvWorkstationProject[]> {
  const payload = await apiJson<ApiResponse<EvWorkstationProject[]>>(
    "/v1/ev-workstation/follow-ups"
  );
  return payload.data ?? [];
}

export async function getEvSummary(): Promise<EvWorkstationSummary | null> {
  const payload = await apiJson<ApiResponse<EvWorkstationSummary>>("/v1/ev-workstation/summary");
  return payload.data ?? null;
}

export async function getEvBrief(): Promise<EvBriefPayload | null> {
  const payload = await apiJson<ApiResponse<EvBriefPayload>>("/v1/ev-workstation/brief");
  return payload.data ?? null;
}

export async function getEvContacts(
  params: EvContactSearchParams = {}
): Promise<EvContactSearchResult[]> {
  const payload = await apiJson<ApiResponse<EvContactSearchResult[]>>(
    `/v1/ev-workstation/contacts${buildContactQueryString(params)}`
  );
  return payload.data ?? [];
}

export async function captureTelegramRelationship(
  body: EvCaptureTelegramRelationshipRequest
): Promise<unknown> {
  const payload = await apiJson<ApiResponse<unknown>>("/v1/outreach/capture/telegram", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return payload.data ?? null;
}

export async function captureTwitterRelationship(
  body: EvCaptureTwitterRelationshipRequest
): Promise<unknown> {
  const payload = await apiJson<ApiResponse<unknown>>("/v1/outreach/capture/twitter", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return payload.data ?? null;
}

export async function updateOutreachRelationship(
  relationshipId: string,
  body: EvRelationshipUpdateRequest
): Promise<unknown> {
  const payload = await apiJson<ApiResponse<unknown>>(
    `/v1/outreach/relationships/${relationshipId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  return payload.data ?? null;
}

export async function updateOutreachRelationshipTarget(
  relationshipId: string,
  body: EvRelationshipTargetUpdateRequest
): Promise<unknown> {
  const payload = await apiJson<ApiResponse<unknown>>(
    `/v1/outreach/relationships/${relationshipId}/target`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  return payload.data ?? null;
}

export async function attachLinkedInIdentityToRelationship(
  relationshipId: string,
  body: EvLinkedInAttachRequest
): Promise<unknown> {
  const payload = await apiJson<ApiResponse<unknown>>(
    `/v1/outreach/relationships/${relationshipId}/identities/linkedin`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  return payload.data ?? null;
}

export async function recordTouchpoints(
  body: EvTouchpointRequest | EvTouchpointRequest[]
): Promise<unknown> {
  const payload = await apiJson<ApiResponse<unknown>>("/v1/outreach/touchpoints", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return payload.data ?? null;
}

export async function appendMessageLog(body: EvMessageLogRequest): Promise<unknown> {
  const payload = await apiJson<ApiResponse<unknown>>("/v1/outreach/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return payload.data ?? null;
}

export async function updateRelationshipFollowUp(body: {
  relationshipId: string;
  followUpDate: string;
}): Promise<unknown> {
  const payload = await apiJson<ApiResponse<unknown>>("/v1/outreach/follow-up", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return payload.data ?? null;
}

export async function bulkImportTwitterHandles(body: {
  targetType: "project" | "equity_product" | "standalone";
  targetId?: string;
  targetLabel: string;
  handles: string[];
}): Promise<unknown> {
  const payload = await apiJson<ApiResponse<unknown>>("/v1/outreach/contacts/twitter-handles", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return payload.data ?? null;
}

export async function getTelegramMtprotoStatus(): Promise<EvTelegramMtprotoStatusPayload | null> {
  const payload = await apiJson<ApiResponse<EvTelegramMtprotoStatusPayload>>(
    "/v1/telegram/mtproto/status"
  );
  return payload.data ?? null;
}

export async function testTelegramMtprotoCredentials(body: {
  apiId: number;
  apiHash: string;
}): Promise<{ ok: true } | null> {
  const payload = await apiJson<ApiResponse<{ ok: true }>>(
    "/v1/telegram/mtproto/test-credentials",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  return payload.data ?? null;
}

export async function startTelegramMtprotoAuth(body: {
  apiId: number;
  apiHash: string;
  phoneNumber: string;
}): Promise<EvTelegramMtprotoStatusPayload | null> {
  const payload = await apiJson<ApiResponse<EvTelegramMtprotoStatusPayload>>(
    "/v1/telegram/mtproto/start-auth",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  return payload.data ?? null;
}

export async function verifyTelegramMtprotoAuth(body: {
  code: string;
  password?: string | null;
}): Promise<EvTelegramMtprotoStatusPayload | null> {
  const payload = await apiJson<ApiResponse<EvTelegramMtprotoStatusPayload>>(
    "/v1/telegram/mtproto/verify-auth",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  return payload.data ?? null;
}

export async function disconnectTelegramMtproto(): Promise<EvTelegramMtprotoStatusPayload | null> {
  const payload = await apiJson<ApiResponse<EvTelegramMtprotoStatusPayload>>(
    "/v1/telegram/mtproto/disconnect",
    { method: "POST" }
  );
  return payload.data ?? null;
}

export async function sendTelegramMtprotoMessage(
  body: EvTelegramSendRequest
): Promise<EvTelegramSendPayload | null> {
  const payload = await apiJson<ApiResponse<EvTelegramSendPayload>>(
    "/v1/telegram/mtproto/send-message",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  return payload.data ?? null;
}

export async function getXConnectionStatus(): Promise<EvXConnectionStatusPayload | null> {
  const payload = await apiJson<ApiResponse<EvXConnectionStatusPayload>>("/v1/me/x/status");
  return payload.data ?? null;
}

export async function startXConnect(): Promise<{
  authorizationUrl: string;
  requiredScopes: string[];
} | null> {
  const payload = await apiJson<
    ApiResponse<{ authorizationUrl: string; requiredScopes: string[] }>
  >("/v1/me/x/connect/start", { method: "POST" });
  return payload.data ?? null;
}

export async function disconnectXConnection(): Promise<EvXConnectionStatusPayload | null> {
  const payload = await apiJson<ApiResponse<EvXConnectionStatusPayload>>("/v1/me/x/disconnect", {
    method: "POST",
  });
  return payload.data ?? null;
}

export async function getXOutreachPromptSettings(): Promise<EvXPromptSettingsPayload | null> {
  const payload = await apiJson<ApiResponse<EvXPromptSettingsPayload>>(
    "/v1/settings/outreach-prompts/x"
  );
  return payload.data ?? null;
}

export async function saveXOutreachPromptSettings(
  template: string
): Promise<EvXPromptSettingsPayload | null> {
  const payload = await apiJson<ApiResponse<EvXPromptSettingsPayload>>(
    "/v1/settings/outreach-prompts/x",
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ template }),
    }
  );
  return payload.data ?? null;
}

export async function generateXPromptExamples(
  projectIds: string[]
): Promise<{ examples: Array<{ projectId: string; prompt: string }> } | null> {
  const payload = await apiJson<
    ApiResponse<{ examples: Array<{ projectId: string; prompt: string }> }>
  >("/v1/settings/outreach-prompts/x/examples", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projectIds }),
  });
  return payload.data ?? null;
}

export async function resolveXHandle(handle: string): Promise<EvXResolveHandlePayload | null> {
  const payload = await apiJson<ApiResponse<EvXResolveHandlePayload>>("/v1/x/resolve-handle", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ handle }),
  });
  return payload.data ?? null;
}

export async function previewXMessage(body: EvXPreviewRequest): Promise<EvXPreviewPayload | null> {
  const payload = await apiJson<ApiResponse<EvXPreviewPayload>>("/v1/x/messages/preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return payload.data ?? null;
}

export async function sendXMessage(body: EvXSendRequest): Promise<EvXSendPayload | null> {
  const payload = await apiJson<ApiResponse<EvXSendPayload>>("/v1/x/messages/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return payload.data ?? null;
}

export async function getXThread(relationshipId: string): Promise<EvXMessageRecord[]> {
  const payload = await apiJson<ApiResponse<EvXMessageRecord[]>>(
    `/v1/x/messages/thread/${relationshipId}`
  );
  return payload.data ?? [];
}

export async function getXAuditTrail(relationshipId: string): Promise<EvXAuditRecord[]> {
  const payload = await apiJson<ApiResponse<EvXAuditRecord[]>>(
    `/v1/x/messages/audit/${relationshipId}`
  );
  return payload.data ?? [];
}

export async function listXQueueContacts(
  params: { search?: string; limit?: number } = {}
): Promise<EvXQueueContact[]> {
  const searchParams = new URLSearchParams();
  if (params.search?.trim()) searchParams.set("search", params.search.trim());
  if (params.limit) searchParams.set("limit", String(params.limit));
  const query = searchParams.toString();
  const payload = await apiJson<ApiResponse<EvXQueueContact[]>>(
    `/v1/x/queue-messages/queue-contacts${query ? `?${query}` : ""}`
  );
  return payload.data ?? [];
}

export async function previewXQueueDm(body: EvXPreviewRequest): Promise<EvXPreviewPayload | null> {
  const payload = await apiJson<ApiResponse<EvXPreviewPayload>>("/v1/x/queue-messages/preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return payload.data ?? null;
}

export async function sendXQueueDm(body: {
  draftId?: string | null;
  relationshipId?: string | null;
  body?: string | null;
  sendSource?: string;
}): Promise<EvXSendPayload | null> {
  const payload = await apiJson<ApiResponse<EvXSendPayload>>("/v1/x/queue-messages/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return payload.data ?? null;
}

export async function listXQueueDrafts(params: { status?: string } = {}): Promise<EvXDraft[]> {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set("status", params.status);
  const query = searchParams.toString();
  const payload = await apiJson<ApiResponse<EvXDraft[]>>(
    `/v1/x/queue-messages/drafts${query ? `?${query}` : ""}`
  );
  return payload.data ?? [];
}

export async function getXQueueDraft(draftId: string): Promise<EvXDraft | null> {
  const payload = await apiJson<ApiResponse<EvXDraft>>(`/v1/x/queue-messages/drafts/${draftId}`);
  return payload.data ?? null;
}

export async function discardXQueueDraft(draftId: string): Promise<EvXDraft | null> {
  const payload = await apiJson<ApiResponse<EvXDraft>>(
    `/v1/x/queue-messages/drafts/${draftId}/discard`,
    { method: "POST" }
  );
  return payload.data ?? null;
}

export async function repairXQueueDraft(draftId: string): Promise<EvXDraft | null> {
  const payload = await apiJson<ApiResponse<EvXDraft>>(
    `/v1/x/queue-messages/drafts/${draftId}/repair`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: "Operator requested repair" }),
    }
  );
  return payload.data ?? null;
}

export async function getEvGoogleConnectionStatus(): Promise<EvGoogleConnectionStatusPayload | null> {
  const payload =
    await apiJson<ApiResponse<EvGoogleConnectionStatusPayload>>("/v1/me/google/status");
  return payload.data ?? null;
}

export async function startEvGoogleConnect(): Promise<EvGoogleConnectStartPayload | null> {
  const payload = await apiJson<ApiResponse<EvGoogleConnectStartPayload>>(
    "/v1/me/google/connect/start",
    {
      method: "POST",
    }
  );
  return payload.data ?? null;
}

export async function disconnectEvGoogleConnection(): Promise<EvGoogleConnectionStatusPayload | null> {
  const payload = await apiJson<ApiResponse<EvGoogleConnectionStatusPayload>>(
    "/v1/me/google/disconnect",
    {
      method: "POST",
    }
  );
  return payload.data ?? null;
}

export async function refreshEvGoogleConnection(
  scope: "gmail" | "calendar" | "all" = "all"
): Promise<EvGoogleRefreshPayload | null> {
  const payload = await apiJson<ApiResponse<EvGoogleRefreshPayload>>("/v1/me/google/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scope }),
  });
  return payload.data ?? null;
}

export async function createEvGoogleGmailDraft(
  request: EvGoogleGmailDraftRequest
): Promise<EvGoogleGmailDraftPayload | null> {
  const payload = await apiJson<ApiResponse<EvGoogleGmailDraftPayload>>(
    "/v1/me/google/gmail/draft",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    }
  );
  return payload.data ?? null;
}

export async function sendEvGoogleGmail(
  request: EvGoogleGmailSendRequest
): Promise<EvGoogleGmailSendPayload | null> {
  const payload = await apiJson<ApiResponse<EvGoogleGmailSendPayload>>("/v1/me/google/gmail/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  return payload.data ?? null;
}

export async function createEvGoogleCalendarEvent(
  request: EvGoogleCalendarEventCreateRequest
): Promise<EvGoogleCalendarEventPayload | null> {
  const payload = await apiJson<ApiResponse<EvGoogleCalendarEventPayload>>(
    "/v1/me/google/calendar/events",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    }
  );
  return payload.data ?? null;
}

export async function updateEvGoogleCalendarEvent(
  eventId: string,
  request: EvGoogleCalendarEventUpdateRequest
): Promise<EvGoogleCalendarEventPayload | null> {
  const payload = await apiJson<ApiResponse<EvGoogleCalendarEventPayload>>(
    `/v1/me/google/calendar/events/${eventId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    }
  );
  return payload.data ?? null;
}

export async function queueEvHandoff(projectId: string): Promise<EvWorkstationDetail | null> {
  const payload = await apiJson<ApiResponse<EvWorkstationDetail>>(
    `/v1/ev-workstation/projects/${projectId}/handoff`,
    {
      method: "POST",
    }
  );

  return payload.data ?? null;
}

export async function retryEvHandoff(handoffId: string): Promise<EvWorkstationDetail | null> {
  const payload = await apiJson<ApiResponse<EvWorkstationDetail>>(
    `/v1/ev-workstation/handoffs/${handoffId}/retry`,
    {
      method: "POST",
    }
  );

  return payload.data ?? null;
}

export async function getEvOwnerOptions(): Promise<AdminOwnerOption[]> {
  const payload = await apiJson<ApiResponse<AdminOwnerOption[]>>("/v1/admin/users");
  return (payload.data ?? []).filter((user) => user.status === "active");
}
